import { useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";

export const SalesCard = ({
  count,
  percentageValue
}: {
  count: number;
  percentageValue: number;
}) => {
  const navigate = useNavigate();

  // Calculate the difference from previous day
  const calculateDifference = () => {
    if (count === 0) return 0;
    // If percentageValue is positive, calculate: current - previous
    // previous = current / (1 + percentage/100)
    const previousValue = count / (1 + percentageValue / 100);
    return Math.abs(count - previousValue);
  };

  const difference = calculateDifference();
  const formattedDifference = difference.toFixed(2);
  const formattedCount = count.toFixed(2);

  // Determine the message based on scenarios
  const getMessage = () => {
    if (count === 0) {
      return "No sales yesterday — let's make today count!";
    } else if (percentageValue > 0) {
      return `You earned $${formattedCount} yesterday — that's $${formattedDifference} more than the day before! 🎉`;
    } else if (percentageValue < 0) {
      return `You earned $${formattedCount} yesterday — that's $${formattedDifference} less than the day before.`;
    } else {
      return `You earned $${formattedCount} yesterday — same as the day before.`;
    }
  };

  return (
    <div className="group relative px-5 py-3.5 rounded-[20px] bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm border border-border/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgba(18,128,121,0.15)] transition-all duration-300 hover:scale-[1.03] overflow-hidden">
      {/* Hover glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-2xl">💰</span>
            <h3 className="text-sm font-medium text-muted-foreground">
              Sales Yesterday
            </h3>
          </div>

          <div className="mb-1.5">
            <p className="text-3xl font-bold text-foreground tracking-tight">
              $ {formattedCount}
            </p>
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed mb-3">
            {getMessage()}
          </p>

          {/* View More Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate("/reports/sales/overview");
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors group/btn"
          >
            View More
            <ArrowRight className="w-3 h-3 transition-transform group-hover/btn:translate-x-0.5" />
          </button>
        </div>

        {/* Mini sparkline */}
        <div className="flex items-end gap-0.5 h-12 ml-4 flex-shrink-0">
          {[40, 65, 55, 80, 70, 90, 100].map((height, i) => (
            <div
              key={i}
              className="w-2 bg-gradient-to-t from-teal-500 to-teal-400 rounded-t animate-fade-in"
              style={{
                height: `${height}%`,
                animationDelay: `${i * 50}ms`,
                minHeight: "8px"
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
